"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  ActingTenant,
  getActingTenant,
  setActingTenant as persistActingTenant,
} from "@/lib/acting-tenant";
import { isSuperAdmin } from "@/lib/auth";
import { useSession } from "next-auth/react";

type ActingTenantContextValue = {
  actingTenant: ActingTenant | null;
  /** Persist acting tenant and reload so dashboard caches refresh (header switcher). */
  setActingTenant: (tenant: ActingTenant | null) => void;
  /** Persist acting tenant without reload (e.g. while browsing admin org pages). */
  syncActingTenant: (tenant: ActingTenant | null) => void;
  canSwitch: boolean;
};

const ActingTenantContext = createContext<ActingTenantContextValue>({
  actingTenant: null,
  setActingTenant: () => {},
  syncActingTenant: () => {},
  canSwitch: false,
});

export function ActingTenantProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  const [actingTenant, setActingTenantState] = useState<ActingTenant | null>(null);

  const canSwitch = isSuperAdmin(session?.user?.roles);

  useEffect(() => {
    if (status === "unauthenticated") {
      // Keep sessionStorage so "View as" survives session expiry / sign-in redirect.
      setActingTenantState(null);
      return;
    }
    if (status !== "authenticated") return;
    if (!canSwitch) {
      persistActingTenant(null);
      setActingTenantState(null);
      return;
    }
    setActingTenantState(getActingTenant());
  }, [canSwitch, status]);

  const syncActingTenant = useCallback(
    (tenant: ActingTenant | null) => {
      if (!canSwitch && tenant) return;
      persistActingTenant(tenant);
      setActingTenantState(tenant);
    },
    [canSwitch],
  );

  const setActingTenant = useCallback(
    (tenant: ActingTenant | null) => {
      syncActingTenant(tenant);
      window.location.reload();
    },
    [syncActingTenant],
  );

  return (
    <ActingTenantContext.Provider
      value={{ actingTenant, setActingTenant, syncActingTenant, canSwitch }}
    >
      {children}
    </ActingTenantContext.Provider>
  );
}

export function useActingTenant() {
  return useContext(ActingTenantContext);
}
